/**
 * @module ResultadoPago
 * @description Página de resultado de transacción ePayco.
 * Se muestra cuando el usuario es redirigido de vuelta desde ePayco tras completar un pago.
 * Consulta el estado de la transacción mediante la referencia ePayco (ref_payco)
 * obtenida de los query params de la URL.
 *
 * Flujo:
 * 1. Extrae ref_payco de los query params de la URL
 * 2. Consulta la API pública de ePayco para validar el resultado
 * 3. Muestra animación de éxito (CheckRounded) o rechazo (DeclinedRounded)
 * 4. Presenta el detalle completo de la transacción
 * 5. Ofrece enlaces para realizar otro pago o ver el comprobante
 *
 * Si no hay ref_payco válido en la URL, redirige al inicio (/).
 */

import { FC, useState, useEffect } from "react";
import axios from "axios";
import { respuestaEpayco, toAbsoluteUrl } from "../../helpers";
import { CheckRounded, DeclinedRounded } from "../../components/animaciones";
import { ResultadoTransaccion } from "./DetailPago";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MyLoader } from "app/modules/comfeModules/MyLoader";

/**
 * Consulta el resultado de una transacción ePayco a través de su API pública.
 * Extrae el ref_payco de los query params de la URL y consulta el endpoint
 * de validación de ePayco. Si la consulta es exitosa, actualiza el estado
 * con los datos de la respuesta. Si no hay ref válido, redirige al inicio.
 *
 * @param ref_payco - Referencia de pago ePayco actual (estado)
 * @param setRef_payco - Setter para actualizar la referencia
 * @param setRespEpayco - Setter para almacenar la respuesta completa de ePayco
 * @param stpb - Setter para controlar el overlay de carga
 */
const consultarResultadoPayco = async (
  ref_payco: string,
  setRef_payco: React.Dispatch<React.SetStateAction<string>>,
  setRespEpayco: React.Dispatch<React.SetStateAction<respuestaEpayco>>,
  stpb: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const queryParams = new URLSearchParams(window.location.search);
  const ref = queryParams.get("ref_payco");

  if (ref !== null && ref !== "undefined") {
    stpb(true);
    setRef_payco(ref);
    var url_payco = "https://secure.epayco.co/validation/v1/reference/" + ref;
    const resp = await axios.get(url_payco);
    if (resp.status === 200) {
      const data: respuestaEpayco = resp.data.data;
      setRespEpayco(data);
      stpb(false);
    } else {
      window.location.href = "/";
    }
  } else {
    window.location.href = "/";
  }
};

/**
 * ResultadoPago - Componente de página completa para mostrar el resultado del pago.
 * Inicializa un estado vacío de respuesta ePayco y lo actualiza al consultar la API.
 * Renderiza animación de estado (éxito/rechazo), detalle de transacción y acciones.
 */
const ResultadoPago: FC = () => {
  /** Estado inicial vacío para la respuesta de ePayco */
  const a: respuestaEpayco = {
    x_cust_id_cliente: "",
    x_ref_payco: "",
    x_id_factura: "",
    x_id_invoice: "",
    x_description: "",
    x_amount: 0,
    x_amount_country: 0,
    x_amount_ok: 0,
    x_tax: 0,
    x_tax_ico: 0,
    x_amount_base: "",
    x_currency_code: "",
    x_bank_name: "",
    x_cardnumber: "",
    x_quotas: "",
    x_respuesta: "",
    x_response: "",
    x_approval_code: "",
    x_transaction_id: "",
    x_fecha_transaccion: "",
    x_transaction_date: "",
    x_cod_respuesta: "",
    x_cod_response: "",
    x_response_reason_text: "",
    x_cod_transaction_state: "",
    x_transaction_state: "",
    x_errorcode: "",
    x_franchise: "",
    x_business: "",
    x_customer_doctype: "",
    x_customer_document: "",
    x_customer_name: "",
    x_customer_lastname: "",
    x_customer_email: "",
    x_customer_phone: "",
    x_customer_movil: "",
    x_customer_ind_pais: "",
    x_customer_country: "",
    x_customer_city: "",
    x_customer_address: "",
    x_customer_ip: "",
    x_signature: "",
    x_test_request: "",
    x_extra1: "",
    x_extra2: "",
    x_extra3: "",
    x_extra4: "",
    x_extra5: "",
    x_extra6: "",
    x_extra7: "",
    x_extra8: "",
    x_extra9: "",
    x_extra10: "",
    x_type_payment: "",
  };

  const [respEpayco, setRespEpayco] = useState(a);
  const [ref_payco, setRef_payco] = useState("");
  const [pantallaBloqueada, setPantallaBloqueada] = useState(false);

  useEffect(() => {
    consultarResultadoPayco(
      ref_payco,
      setRef_payco,
      setRespEpayco,
      setPantallaBloqueada
    );
  }, [ref_payco]);

  return (
    <MyLoader loading={pantallaBloqueada}>
      <div id="contenedor-principal-pagos-online">
        <div className="Container-fluid pt-18 ">
          <div className="">
            <div className={"col-xs-12 col-sm-12 col-md-12 col-lg-12"}>
              <div
                className="card card-custom bg-white rounded-4 shadow
                         col-xs-12 offset-xs-0 
                         col-sm-12 offset-sm-0 
                         col-md-10 offset-md-1
                         col-lg-6 offset-lg-3"
                id="contenedor-pago-virtual"
              >
                <div className="card-header2">
                  <img
                    className="img-fluid o-logo ps-8"
                    alt="Comfenalco"
                    src={toAbsoluteUrl(
                      "/media/logos/logoComfenalcoHorizontal.svg"
                    )}
                    width={"42%"}
                  ></img>
                  <h3 className="card-title fs-1 ms-5">Pagos en línea</h3>
                  <div className="card-toolbar"></div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12 text-center fs-1 mt-8">
                      {respEpayco.x_response}
                    </div>
                    <div className="col-md-12">
                      {respEpayco.x_response === "Aceptada" && <CheckRounded />}
                      {respEpayco.x_response === "Rechazada" && (
                        <DeclinedRounded />
                      )}
                    </div>
                    <div className="col-md-12 mt-12">
                      <ResultadoTransaccion resultadoPago={respEpayco} />
                    </div>
                    <div className="d-flex flex-stack pt-10">
                      <div className="mb-4 ">
                        <a
                          className="btn btn-comfe-light rounded-pill me-3"
                          href="/"
                        >
                          <FontAwesomeIcon icon={"sync"}></FontAwesomeIcon>{" "}
                          Realizar otro pago
                        </a>
                      </div>
                      <div className="me-2 ">
                        <a
                          className="btn btn-comfe rounded-pill "
                          target={"_blank"}
                          rel="noreferrer"
                          href={
                            "/comprobantePago/" +
                            respEpayco.x_ref_payco +
                            "/" +
                            respEpayco.x_cust_id_cliente
                          }
                        >
                          <FontAwesomeIcon icon={"search"}></FontAwesomeIcon>{" "}
                          Ver comprobante de pago
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MyLoader>
  );
};

export { ResultadoPago };
